import { isAiSpendCapError } from '../../utils/aiChatErrors';

type ChatApiError = Error & { code?: string; meta?: Record<string, unknown> };

export async function consumeChatStream(
  response: Response,
  { onDelta, onDone, onStatus = (_status: string) => {} }: {
    onDelta: (delta: string) => void;
    onDone: (data: any) => void;
    onStatus?: (status: string) => void;
  }
) {
  if (!response.ok) {
    let message = `Stream failed (${response.status})`;
    try {
      const errBody = await response.json();
      message = errBody.message || message;
      if (errBody.code) {
        const e = new Error(message) as ChatApiError;
        e.code = errBody.code;
        e.meta = errBody.meta;
        throw e;
      }
      if (isAiSpendCapError({ message })) {
        const e = new Error(message) as ChatApiError;
        e.code = 'AI_SPEND_CAP';
        throw e;
      }
    } catch (parseErr) {
      if (parseErr && typeof parseErr === 'object' && 'code' in parseErr && (parseErr as ChatApiError).code) {
        throw parseErr;
      }
    }
    throw new Error(message);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('Streaming not supported');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (!payload || payload === '[DONE]') continue;
      const data = JSON.parse(payload);
      if (data.error) {
        const e = new Error(data.error) as ChatApiError;
        if (data.code) e.code = data.code;
        else if (isAiSpendCapError({ message: data.error })) e.code = 'AI_SPEND_CAP';
        throw e;
      }
      if (data.status) onStatus(data.status);
      if (data.delta) onDelta(data.delta);
      if (data.done) onDone(data);
    }
  }
}
